import { Modal, Tooltip } from '@intelie/ui'
import Label from 'live/components/forms/ui/label'
import Select from 'live/components/forms/ui/select'
import i18n from 'live/services/i18n'
import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { DashboardApi, DashboardClient, PackageFeatureResource } from '../http/api'
import { packageManagerHttpServicesClient } from '../http/services'
import PerspectiveSelect from '../components/PerspectiveSelect'

export type Data = {
    confirmed?: boolean
    perspectiveId?: number
    sectionActive?: number
    overwriteEntityId?: number
}

type Props = {
    isOpen: boolean
    perspectiveId?: number
    overwriteEntityId?: number
    feature: PackageFeatureResource
    isUnpacking: boolean
    unpackage(event: React.MouseEvent<HTMLAnchorElement>): void
    callbackConfirm(res): Promise<void>
    cancelUnpacking(event: React.MouseEvent<HTMLAnchorElement>): void
}

export const SECTION_OPTION_UPDATE_DASHBOARD = 1
export const SECTION_OPTION_CREATE_DASHBOARD = 2

type ValidationFields = {
    dashboard: boolean
    perspective: boolean // true is valid, false is invalid
}

const initValidationFields: ValidationFields = {
    dashboard: true,
    perspective: true
}

const InfoTooltip = ({}) => {
    return (
        <Tooltip
            overlay={
                <>
                    {i18n('For security reasons, only')} <strong>{i18n('dashboards')}</strong>{' '}
                    {i18n(`with the same name as defined in the package manifest are displayed`)}.
                </>
            }
            placement="top"
            textAlign="center"
        >
            <i className="fal fa-info-circle" style={{ marginLeft: 5 }} />
        </Tooltip>
    )
}

const ModalFeatureConfig = ({
    isOpen,
    feature,
    perspectiveId,
    overwriteEntityId,
    isUnpacking = false,
    callbackConfirm,
    unpackage,
    cancelUnpacking
}: Props) => {
    const dashboardClient: DashboardApi = new DashboardClient()
    const [dashboard, setDashboard] = useState<string>()
    const [perspective, setPerspective] = useState<number>()
    const [validationFields, setValidationFields] = useState<ValidationFields>(initValidationFields)
    const [sectionActive, setSectionActive] = useState<number>(SECTION_OPTION_CREATE_DASHBOARD)
    const [dashboardsOptions, setDashboardsOptions] = useState([])
    const [entityInstalled, setEntityInstalled] = useState<Record<string, any>>()
    const [label, setLabel] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(true)
    const [dashboardNotHasEntityId, setDashboardNotHasEntityId] = useState<boolean>(false)

    const firstConfig = feature.settings === undefined

    useEffect(() => {
        init()
    }, [isOpen])

    useEffect(() => {
        const labelPrefix = i18n(firstConfig === false && entityInstalled?.id ? 'Edit' : 'Activate')
        let version = feature.version ? `V${feature.version}` : ''
        version = version ? `[${version}]` : ''
        const label = labelPrefix + ' ' + (feature.label || feature.name) + ' ' + version
        setLabel(label)

        if (firstConfig === false) {
            const dashboard = dashboardsOptions?.find(ele => ele.value == entityInstalled?.id)

            const dashboardsNotHasId = entityInstalled?.id !== undefined && dashboard === undefined

            setDashboardNotHasEntityId(dashboardsNotHasId)

            handleChangeDashboard(dashboard)
        }
    }, [entityInstalled, dashboardsOptions])

    const fillDashboardOptions = async () => {
        const response = await dashboardClient.listByName(feature?.label)
        const dashboardsOptions =
            response
                ?.filter((ele: Record<string, any>) => !ele?.apiId || ele?.apiId === feature.uuid)
                .map(ele => ({ label: ele?.title + ' - ' + ele.id, value: ele?.id?.toString() })) || []

        setDashboardsOptions(dashboardsOptions)
    }

    const init = async () => {
        setLoading(true)

        setValidationFields(initValidationFields)

        if (isOpen) fillDashboardOptions()
        if (perspectiveId) setPerspective(perspectiveId)

        const sectionActive = overwriteEntityId ? SECTION_OPTION_UPDATE_DASHBOARD : SECTION_OPTION_CREATE_DASHBOARD
        setSectionActive(sectionActive)

        if (isOpen && firstConfig === false) {
            const entity = await packageManagerHttpServicesClient.searchEntityByUUid(feature.uuid, feature.type)

            setEntityInstalled(entity)

            if (!overwriteEntityId && entity?.id) {
                setDashboard(entity?.id.toString())
            } else if (overwriteEntityId) {
                setDashboard(overwriteEntityId.toString())
            }
        }

        setTimeout(() => {
            setLoading(false)
        }, 3000)
    }

    const handleChangeDashboard = value => {
        setDashboard(value)
        setValidationFields(curr => {
            curr.dashboard = true

            return curr
        })

        setSectionActive(value === undefined ? SECTION_OPTION_CREATE_DASHBOARD : SECTION_OPTION_UPDATE_DASHBOARD)
    }

    const handleChangePerspective = (value: number) => {
        setValidationFields(curr => {
            curr.perspective = true

            return curr
        })

        setPerspective(+value)
    }

    const handleSectionActive = (value: number) => {
        setSectionActive(value)

        if (value === SECTION_OPTION_CREATE_DASHBOARD) {
            setValidationFields(curr => {
                curr.dashboard = true

                return curr
            })
        }
    }

    const handleCloseModal = (confirmed: boolean) => {
        const data: Data = { confirmed }

        if (perspective) data.perspectiveId = perspective

        if (dashboard) data.overwriteEntityId = Number.parseInt(dashboard, 10)

        if (sectionActive === SECTION_OPTION_CREATE_DASHBOARD) {
            data.overwriteEntityId = undefined
        }

        setDashboard(undefined)
        setPerspective(undefined)

        return data
    }

    const isFieldsValid = (): boolean => {
        const aux = { ...validationFields }

        aux.perspective = perspective !== undefined
        aux.dashboard = sectionActive === SECTION_OPTION_UPDATE_DASHBOARD ? dashboard !== undefined : true

        setValidationFields(aux)

        return Object.keys(aux).every(key => aux[key] === true)
    }

    const save = () => {
        const canSave = isFieldsValid()

        if (canSave) return handleCloseModal(true)

        return null
    }

    const disabled = dashboardsOptions?.length === 0

    return (
        <Modal
            show={isOpen}
            closable={true}
            size={621}
            onHide={() => callbackConfirm(res => res(handleCloseModal(false)))}
            onSave={() => callbackConfirm(res => res(save()))}
        >
            <Modal.Header closeButton={true} title={label} closeLabel={'Close'} />

            <Wrapper isLoading={loading}>
                {loading ? (
                    <div className="loading" />
                ) : (
                    <>
                        {(firstConfig || dashboardNotHasEntityId || (!isUnpacking && !entityInstalled?.id)) && (
                            <Section>
                                <h3>{i18n('Dashboard selection')}</h3>

                                <SectionCard
                                    active={sectionActive === SECTION_OPTION_UPDATE_DASHBOARD}
                                    aria-disabled={disabled}
                                    disabledCard={disabled}
                                    onClick={() => !disabled && handleSectionActive(SECTION_OPTION_UPDATE_DASHBOARD)}
                                >
                                    <div className="div-first-child">
                                        <i className="fas fa-undo" />
                                    </div>

                                    <div
                                        className={
                                            validationFields.dashboard === false
                                                ? 'div-second-child has-error'
                                                : 'div-second-child'
                                        }
                                    >
                                        <Label
                                            className="control-label"
                                            required={sectionActive === SECTION_OPTION_UPDATE_DASHBOARD}
                                        >
                                            {i18n('Overwrite dashboard selection')}
                                            <InfoTooltip />
                                        </Label>

                                        <span className="subtitle control-label">
                                            {i18n(
                                                overwriteEntityId !== undefined && dashboard != overwriteEntityId
                                                    ? 'Changing current selected dashboard will overwrite any existing data or widgets'
                                                    : 'This will overwrite any existing data or widgets on the selected dashboard'
                                            )}
                                        </span>

                                        <br />

                                        <WrapperSelect
                                            id="wrapper-overwrite-dashboard-selection"
                                            hasError={validationFields.dashboard === false}
                                            className={validationFields.dashboard === false ? 'has-error' : ''}
                                        >
                                            <Select
                                                value={dashboard}
                                                clearable={false}
                                                options={dashboardsOptions}
                                                onChange={handleChangeDashboard}
                                                searchable={dashboardsOptions.length > 5}
                                                disabled={disabled}
                                                placeholder={i18n(
                                                    dashboardsOptions?.length === 0 ? 'No options available' : ''
                                                )}
                                            />

                                            {validationFields.dashboard === false && (
                                                <span className="help-block">{i18n('dashboard must not be null')}</span>
                                            )}
                                        </WrapperSelect>
                                    </div>

                                    <div className="div-third-child">
                                        <RadioButton
                                            disabled={disabled}
                                            active={sectionActive === SECTION_OPTION_UPDATE_DASHBOARD}
                                        />
                                    </div>
                                </SectionCard>

                                <SectionCard
                                    active={sectionActive === SECTION_OPTION_CREATE_DASHBOARD}
                                    onClick={() => handleSectionActive(SECTION_OPTION_CREATE_DASHBOARD)}
                                >
                                    <div className="div-first-child">
                                        <i className="fas fa-plus" />
                                    </div>

                                    <div className="div-second-child">
                                        <Label>{i18n('Create new dashboard')}</Label>
                                        <span className="subtitle">
                                            {i18n(
                                                'This will create a new empty dashboard with the new dashboard configuration'
                                            )}
                                            .
                                        </span>
                                    </div>

                                    <div className="div-third-child">
                                        <RadioButton active={sectionActive === SECTION_OPTION_CREATE_DASHBOARD} />
                                    </div>
                                </SectionCard>
                            </Section>
                        )}

                        {/* {isUnpacking && (
                            <WrapperUnpacking>
                                <li>
                                    <i className="fas fa-box-open break" />
                                </li>
                                <li>{i18n('This feature is set to be unpacked')}</li>
                                <li>
                                    {i18n(
                                        'Just close the window if you want to keep it, or click the button below to cancel the action or refresh the page'
                                    )}
                                    .
                                </li>
                                <a onClick={cancelUnpacking}>{i18n('Cancel unpack')} </a>
                            </WrapperUnpacking>
                        )} */}

                        {!isUnpacking && (
                            <>
                                <Section>
                                    <h3>{i18n('Properties')}</h3>

                                    {firstConfig === false && !dashboardNotHasEntityId && entityInstalled?.id && (
                                        <div style={{ marginBottom: 16 }}>
                                            <WrapperSelect
                                                id="wrapper-selected-dashboard"
                                                hasError={validationFields.dashboard === false}
                                                className={validationFields.dashboard === false ? 'has-error' : ''}
                                            >
                                                <Select
                                                    label={
                                                        <span>
                                                            {i18n('Selected dashboard')}
                                                            <InfoTooltip />
                                                        </span>
                                                    }
                                                    value={dashboard}
                                                    onChange={handleChangeDashboard}
                                                    clearable={false}
                                                    options={dashboardsOptions}
                                                    searchable={dashboardsOptions.length > 5}
                                                />

                                                {validationFields.dashboard === false && (
                                                    <span className="help-block">
                                                        {i18n('dashboard must not be null')}
                                                    </span>
                                                )}
                                            </WrapperSelect>

                                            <span style={{ marginTop: 10 }} className="subtitle">
                                                <i className="fal fa-info-circle" style={{ marginRight: 5 }} />
                                                {i18n(
                                                    'Changing current selected dashboard will overwrite any existing data or widgets'
                                                )}
                                                .
                                            </span>
                                        </div>
                                    )}

                                    <WrapperSelect
                                        hasError={validationFields.perspective === false}
                                        className={validationFields.perspective === false ? 'has-error' : ''}
                                    >
                                        <PerspectiveSelect
                                            value={perspective}
                                            required={true}
                                            label={i18n('Perspective')}
                                            onChange={handleChangePerspective}
                                        />

                                        {validationFields.perspective === false && (
                                            <span className="help-block">{i18n('pespective must not be empty')}</span>
                                        )}
                                    </WrapperSelect>

                                    {firstConfig === false && entityInstalled?.id && (
                                        <span style={{ marginTop: 16 }} className="subtitle">
                                            <i className="fal fa-info-circle" style={{ marginRight: 5 }} />
                                            {i18n(
                                                'Changing current selected perspective will affect current users of this feature'
                                            )}
                                            .
                                        </span>
                                    )}
                                </Section>
                            </>
                        )}
                    </>
                )}
            </Wrapper>

            {!isUnpacking && loading === false && (
                <Modal.Footer saveLabel={i18n('Confirm')} hideLabel={i18n('Cancel')}>
                    {/* {entityInstalled?.id && <Unpackage title="Unpackage the dashboard" click={unpackage} />} */}
                </Modal.Footer>
            )}
        </Modal>
    )
}

export default ModalFeatureConfig

const Section = styled.div`
    .far {
        font-size: 14px;
        font-weight: 900;
        color: ${({ theme }) => theme['gray-600']};
    }

    h3 {
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 16px;
        color: ${({ theme }) => theme['gray-800']};
        font-family: ${({ theme }) => theme['font-semibold']};
    }

    .subtitle {
        font-size: 13px;
        color: ${({ theme }) => theme['gray-800']};
        font-family: ${({ theme }) => theme['font-regular']};
    }
`

const SectionCard = styled.div<{ active: boolean; disabledCard?: boolean }>`
    display: flex;
    padding: 15px 10px;
    border-radius: 3px;
    margin-bottom: 10px;
    border: 1px solid ${({ theme, active }) => (active ? theme['blue-700'] : theme['gray-400'])};
    background-color: ${({ theme, active }) => (active ? theme['blue-200'] : 'transparent')};

    .div-first-child,
    .div-third-child {
        width: 25px;
    }

    .div-second-child {
        flex: 1;
    }

    .div-third-child {
        display: flex;
        justify-content: end;
    }

    ${({ disabledCard }) =>
        !disabledCard &&
        css`
            &:hover {
                cursor: pointer;
                border-color: var(--blue-700);
                background-color: var(--blue-200);

                label {
                    cursor: pointer;
                }
            }
        `}
`

interface RadioButtonProps {
    active: boolean
    disabled?: boolean
}

const RadioButton = ({ active, disabled = false }: RadioButtonProps): JSX.Element => {
    return (
        <RadioButtonWrapper className="radio-button">
            <RadioButtonActive active={active} disabled={disabled} />
        </RadioButtonWrapper>
    )
}

const Unpackage = ({ title, click }) => {
    return <UnpackageLink onClick={click}>{i18n(title)}</UnpackageLink>
}

const WrapperUnpacking = styled.ul`
    height: 200px;
    padding-top: 57px;
    text-align: center;
    padding: 57px 80px 57px 80px;

    li:nth-child(1) {
        font-size: 24px;
        font-weight: 900;
        margin-bottom: 7px;
        color: ${({ theme }) => theme['gray-500']};
    }

    li:nth-child(2) {
        font-size: 13px;
        font-weight: 600;
        margin-bottom: 7px;
        color: ${({ theme }) => theme['gray-800']};
    }

    li:nth-child(3) {
        font-size: 13px;
        font-weight: 400;
        font-style: italic;
        margin-bottom: 7px;
        color: ${({ theme }) => theme['gray-600']};
    }
`

const UnpackageLink = styled.a`
    font-size: 14px !important;
    font-weight: 600 !important;
    outline: none !important;
    text-decoration: none !important;
    color: ${({ theme }) => theme['red-500']} !important;
`

const Wrapper = styled.div<{ isLoading: boolean }>`
    min-height: 269px;
    width: 621px;
    background: ${({ isLoading, theme }) => (isLoading ? theme['gray-0'] : theme['gray-100'])};
    padding: 24px;

    .loading {
        height: 269px;
    }
`

const RadioButtonWrapper = styled.div`
    width: 16px;
    padding: 8%;
    height: 16px;
    display: flex;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    background-color: ${({ theme }) => theme['gray-0']};
    border: 1px solid ${({ theme }) => theme['gray-600']};
`

const RadioButtonActive = styled.div<{ active: boolean; disabled: boolean }>`
    width: 8px;
    height: 8px;
    display: block;
    border-radius: 50%;
    display: ${({ active }) => (active ? 'block' : 'none')};

    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
    background-color: ${({ disabled }) => (disabled ? 'var(--gray-200)' : 'var(--blue-500)')};
`

const WrapperSelect = styled.div<{ hasError: boolean }>`
    .form-group {
        margin-bottom: ${({ hasError }) => (hasError ? '25px' : '4px')} !important;
    }
`
